/**
 * MoonRenderer — dedicated procedural watercolor Moon component for celestial atmosphere.
 *
 * Provides hand-drawn stylized lunar disk art:
 * - Basaltic maria (darker lunar plains) with organic crater groupings.
 * - Spherical limb shading and crisp illuminated edge rim.
 * - Accurate phase silhouettes ('full' | 'crescent' | 'first_quarter' | 'gibbous' | 'new').
 * - Dynamic SVG generation compatible with Pixi texture caches.
 */
import { hasSharedTexture, registerCanvasTexture } from '../../../rendering/PixiSprite';

export type MoonPhase = 'full' | 'crescent' | 'first_quarter' | 'gibbous' | 'new';

export interface MoonRenderOptions {
  size: number;
  color?: number | string;
  mariaColor?: number | string;
  phase?: MoonPhase;
  craters?: boolean;
}

function hexString(c: number | string | undefined, defaultHex: string): string {
  if (typeof c === 'string') return c.startsWith('#') ? c : `#${c}`;
  if (typeof c === 'number') return `#${c.toString(16).padStart(6, '0')}`;
  return defaultHex;
}

/**
 * Generate standalone SVG markup for a stylized lunar body.
 */
export function generateMoonSVG(opts: MoonRenderOptions): string {
  const size = Math.max(16, Math.round(opts.size));
  const phase = opts.phase ?? 'full';
  const craters = opts.craters ?? (phase !== 'new');
  const baseColor = hexString(opts.color, '#e2e5ff');
  const mariaColor = hexString(opts.mariaColor, '#9da6cf');
  const glowRim = '#ffffff';

  // Maria paths in normalized 100x100 viewBox
  const mariaGroup = craters ? `
    <!-- Basaltic lunar maria (Oceanus Procellarum, Mare Imbrium, Mare Serenitatis) -->
    <g fill="${mariaColor}" opacity="0.32">
      <path d="M26 38 Q32 28 42 32 Q48 38 45 46 Q37 52 28 48 Q22 44 26 38 Z" />
      <path d="M54 26 Q66 24 72 33 Q74 42 65 46 Q56 44 52 36 Q50 30 54 26 Z" />
      <path d="M46 54 Q58 50 64 58 Q66 68 56 72 Q44 74 40 66 Q38 58 46 54 Z" />
      <circle cx="34" cy="66" r="5" />
      <circle cx="68" cy="58" r="4.2" />
      <circle cx="38" cy="24" r="3.5" />
      <circle cx="58" cy="72" r="3" />
    </g>` : '';

  // Phase shadow masks
  let phaseShadow = '';
  if (phase === 'crescent') {
    phaseShadow = `
      <!-- Crescent shadow cutting into disk -->
      <path d="M50 2 C23.5 2 2 23.5 2 50 C2 76.5 23.5 98 50 98 C32 82 28 58 35 40 C41 26 50 12 50 2 Z" fill="#0c101d" opacity="0.88" />
      <path d="M50 2 C65 20 62 78 50 98 C76.5 98 98 76.5 98 50 C98 23.5 76.5 2 50 2 Z" fill="#0c101d" opacity="0.88" />`;
  } else if (phase === 'first_quarter') {
    phaseShadow = `
      <!-- Quarter shadow: right half dark -->
      <path d="M50 2 L50 98 C76.5 98 98 76.5 98 50 C98 23.5 76.5 2 50 2 Z" fill="#0c101d" opacity="0.86" />`;
  } else if (phase === 'gibbous') {
    phaseShadow = `
      <!-- Gibbous shadow: small crescent slice on right -->
      <path d="M50 2 C66 22 66 78 50 98 C76.5 98 98 76.5 98 50 C98 23.5 76.5 2 50 2 Z" fill="#0c101d" opacity="0.84" />`;
  } else if (phase === 'new') {
    phaseShadow = `
      <!-- New moon: dark silhouette with faint earthshine -->
      <circle cx="50" cy="50" r="48" fill="#0c101d" opacity="0.94" />`;
  }

  return `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="lunarLimb" cx="42%" cy="40%" r="56%">
      <stop offset="0%" stop-color="${baseColor}" stop-opacity="1" />
      <stop offset="78%" stop-color="${baseColor}" stop-opacity="0.94" />
      <stop offset="100%" stop-color="${mariaColor}" stop-opacity="0.82" />
    </radialGradient>
  </defs>
  <!-- Base illuminated lunar disk -->
  <circle cx="50" cy="50" r="48" fill="url(#lunarLimb)" />
  ${mariaGroup}
  <!-- Crisp outer limb rim -->
  <circle cx="50" cy="50" r="47.5" fill="none" stroke="${glowRim}" stroke-width="1.2" opacity="0.55" />
  ${phaseShadow}
</svg>`;
}

/**
 * Generate a deterministic texture key for the moon sprite.
 */
export function getMoonTextureKey(opts: MoonRenderOptions): string {
  const p = opts.phase ?? 'full';
  const c = typeof opts.color === 'number' ? opts.color.toString(16) : String(opts.color ?? 'default');
  return `celestial_moon_${p}_${c}_${opts.size}`;
}

/**
 * Ensure the moon SVG texture is registered in the shared texture cache.
 * Returns the texture key to consume in ISprite.
 */
export function ensureMoonTexture(opts: MoonRenderOptions): string | null {
  const key = getMoonTextureKey(opts);
  if (hasSharedTexture(key)) return key;

  if (typeof document === 'undefined') return null; // headless environment guard

  try {
    const svg = generateMoonSVG(opts);
    const img = new Image();
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
    const canvas = document.createElement('canvas');
    canvas.width = opts.size;
    canvas.height = opts.size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Synchronous draw if cached/ready, else rasterize on image load
    img.onload = () => {
      ctx.clearRect(0, 0, opts.size, opts.size);
      ctx.drawImage(img, 0, 0, opts.size, opts.size);
      registerCanvasTexture(key, canvas);
    };
    return key;
  } catch {
    return null;
  }
}
